/**
 * Overview tab: run headers, a metric-by-metric comparison grid (value + delta vs baseline +
 * per-metric winner), an auto-generated "who leads where" summary, and grouped charts. All values
 * come from the aligned matrix / run aggregates — no extra fetching.
 */
import { useState, useMemo, Fragment } from "react";
import { Card, Tag, Tooltip, Empty, Switch, Progress } from "antd";
import { SmartMetricsCompareView } from "./SmartMetrics";
import { SmartCardCompareView } from "./SmartCardCompare";
import {
  TrophyFilled,
  BulbOutlined,
  DatabaseOutlined,
  RobotOutlined,
  DashboardOutlined,
  AppstoreOutlined,
  SlidersOutlined,
  SafetyCertificateOutlined,
  PictureOutlined,
  FileTextOutlined,
  SearchOutlined,
  FieldTimeOutlined,
  AccountBookOutlined,
  StarFilled,
  StarOutlined,
  ApiOutlined,
  DollarCircleOutlined,
} from "@ant-design/icons";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  ResponsiveContainer,
  CartesianGrid,
  LabelList,
} from "recharts";
import {
  METRICS,
  bestRunId,
  metricDelta,
  runMetric,
  type CompareMatrix,
  type CompareRun,
  type MetricDef,
} from "../lib/align";
import { BenchmarkConfigDetails } from "../../components/BenchmarkConfigSummary";
import { effectiveInferenceParams } from "@/components/ai-connection/inferenceOptionsOverride";

const statusTag = (status: string) => {
  const color =
    status === "completed"
      ? "green"
      : status === "failed"
        ? "red"
        : status === "running" || status === "pending"
          ? "blue"
          : "default";
  return <Tag color={color}>{status}</Tag>;
};

// Bar height value (percentages scaled to 0–100 so the axis reads naturally).
const chartValue = (def: MetricDef, v: number | null): number | null => {
  if (v == null) return null;
  return def.key === "accuracyRate" || def.key === "successRate" ? v * 100 : v;
};

const MiniBarChart = ({
  def,
  runs,
}: {
  def: MetricDef;
  runs: CompareRun[];
}) => {
  const data = runs.map((r, i) => {
    const short = r.label.length > 14 ? `${r.label.slice(0, 13)}…` : r.label;
    // Two runs of the same config share a label; disambiguate so bars + keys stay unique.
    const duplicated = runs.some((o, j) => j !== i && o.label === r.label);
    return {
      id: r.id,
      name: duplicated ? `${short} #${i + 1}` : short,
      value: chartValue(def, runMetric(r.run, def.key)) ?? 0,
      raw: runMetric(r.run, def.key),
      color: r.color,
    };
  });
  return (
    <Card
      title={
        <span className="text-[13px] font-bold uppercase tracking-wider text-gray-800">
          {def.label}
        </span>
      }
      styles={{ body: { padding: 12 } }}
      className="shadow-[0_4px_20px_rgb(0,0,0,0.03)] border border-gray-200/60 !rounded-2xl overflow-hidden transition-all hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)]"
    >
      <ResponsiveContainer width="100%" height={170}>
        <BarChart
          data={data}
          margin={{ top: 16, right: 8, left: -18, bottom: 0 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="#f0f0f0"
          />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 10 }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
          <RTooltip cursor={{ fill: "#f8fafc" }} />
          <Bar dataKey="value" radius={[4, 4, 0, 0]}>
            {data.map((d) => (
              <Cell key={d.id} fill={d.color} />
            ))}
            <LabelList
              dataKey="raw"
              position="top"
              className="text-[10px]"
              formatter={(v: number) => def.format(v)}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Card>
  );
};

const DeltaBadge = ({
  def,
  value,
  baseline,
}: {
  def: MetricDef;
  value: number | null;
  baseline: number | null;
}) => {
  const d = metricDelta(def, value, baseline);
  if (!d) return null;
  if (d.better === null)
    return <span className="text-gray-300 text-xs ml-1">=</span>;
  const isPositive = d.diff > 0;
  return (
    <span
      className={`text-xs ml-1 font-semibold ${d.better ? "text-emerald-600" : "text-rose-600"}`}
    >
      {isPositive ? "▲" : "▼"} {def.format(Math.abs(d.diff))}
    </span>
  );
};

export const SvgConnections = ({ runs }: { runs: CompareRun[] }) => {
  if (runs.length !== 2) return null;
  // We draw a few stylized bezier curves between the two cards.
  // This is an absolute overlay that sits behind/between the cards.
  return (
    <div className="absolute inset-0 pointer-events-none overflow-visible hidden md:block z-0">
      <svg
        className="w-full h-full"
        preserveAspectRatio="none"
        style={{ overflow: "visible" }}
      >
        <defs>
          <linearGradient id="grad-temp" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop
              offset="0%"
              stopColor={runs[0].color || "#3b82f6"}
              stopOpacity="0.4"
            />
            <stop
              offset="100%"
              stopColor={runs[1].color || "#22c55e"}
              stopOpacity="0.4"
            />
          </linearGradient>
          <linearGradient id="grad-top" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop
              offset="0%"
              stopColor={runs[0].color || "#3b82f6"}
              stopOpacity="0.3"
            />
            <stop
              offset="100%"
              stopColor={runs[1].color || "#22c55e"}
              stopOpacity="0.3"
            />
          </linearGradient>
        </defs>

        {/* Connection for Provider/Model row */}
        <path
          d="M calc(50% - 20px) 70 C 50% 70, 50% 70, calc(50% + 20px) 70"
          fill="none"
          stroke="url(#grad-top)"
          strokeWidth="3"
        />
        {/* Connection for Temperature (crosses down to right) */}
        <path
          d="M calc(50% - 20px) 160 C 50% 160, 50% 160, calc(50% + 20px) 160"
          fill="none"
          stroke="url(#grad-temp)"
          strokeWidth="4"
        />
        {/* Connection for Top-P (crosses up to right) */}
        <path
          d="M calc(50% - 20px) 120 C 50% 120, 50% 120, calc(50% + 20px) 120"
          fill="none"
          stroke="url(#grad-top)"
          strokeWidth="2"
        />
        {/* Connection for Max Tokens */}
        <path
          d="M calc(50% - 20px) 200 C 50% 200, 50% 200, calc(50% + 20px) 200"
          fill="none"
          stroke="url(#grad-top)"
          strokeWidth="2"
        />
        {/* We can make the lines look like the screenshot by adding the criss-cross swoops */}
        <path
          d="M calc(50% - 10px) 150 C 50% 150, 50% 210, calc(50% + 10px) 210"
          fill="none"
          stroke="url(#grad-temp)"
          strokeWidth="3"
          opacity="0.6"
        />
        <path
          d="M calc(50% - 10px) 210 C 50% 210, 50% 150, calc(50% + 10px) 150"
          fill="none"
          stroke="url(#grad-top)"
          strokeWidth="2"
          opacity="0.6"
        />
      </svg>
    </div>
  );
};

const _SmartCardCompareView = ({
  runs,
  absoluteConfigs,
  diffOnly,
}: {
  runs: CompareRun[];
  absoluteConfigs: any[];
  diffOnly: boolean;
}) => {
  const isIdentical = (source: "params" | "judge", key: string) => {
    const vals = absoluteConfigs.map((c) => c[source][key]);
    const unique = new Set(vals);
    return unique.size <= 1;
  };

  const getOpacity = (source: "params" | "judge", key: string) => {
    if (!diffOnly) return "opacity-100";
    return isIdentical(source, key) ? "opacity-50 grayscale" : "opacity-100";
  };

  const MutedDefault = () => (
    <span className="text-gray-400 font-medium text-xs bg-gray-100/50 px-2 py-1 rounded-md inline-block">
      Not Configured
    </span>
  );

  return (
    <div className="relative p-6 bg-slate-50/50 rounded-b-xl overflow-hidden">
      <SvgConnections runs={runs} />

      <div
        className="grid gap-6 relative z-10"
        style={{
          gridTemplateColumns: `repeat(${runs.length}, minmax(0, 1fr))`,
        }}
      >
        {runs.map((r, idx) => {
          const config = absoluteConfigs.find((c) => c.id === r.id);
          if (!config) return null;
          const p = config.params;
          const j = config.judge;

          const themeColor = r.color || (idx === 0 ? "#3b82f6" : "#22c55e");

          // Generate a highly transparent version of the theme color for backgrounds
          // A simple hack is to use the hex color if provided, else default.
          const bgTint = `${themeColor}10`;
          const borderTint = `${themeColor}40`;
          const iconBgTint = `${themeColor}20`;

          return (
            <div
              key={r.id}
              className="rounded-2xl overflow-hidden flex flex-col bg-white transition-all shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] border"
              style={{ borderColor: borderTint }}
            >
              {/* Card Header */}
              <div
                className="px-4 py-3 flex items-center gap-2 text-white font-semibold text-sm justify-center shadow-sm relative overflow-hidden"
                style={{ backgroundColor: themeColor }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-black/10 to-transparent pointer-events-none" />
                <span className="w-2.5 h-2.5 rounded-full bg-white/80 inline-block shadow-sm z-10" />
                <span className="z-10">{r.label}</span>
              </div>

              {/* Card Body */}
              <div
                className="p-5 flex-1 flex flex-col gap-4 relative"
                style={{ backgroundColor: bgTint }}
              >
                {/* Row 1: Provider & Model */}
                <div className="grid grid-cols-2 gap-3">
                  <div
                    className={`bg-white rounded-xl p-3.5 border shadow-sm flex flex-col transition-opacity duration-300 ${getOpacity("params", "provider")}`}
                    style={{ borderColor: borderTint }}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div
                        className="w-7 h-7 rounded-full flex items-center justify-center text-sm"
                        style={{
                          backgroundColor: iconBgTint,
                          color: themeColor,
                        }}
                      >
                        <DatabaseOutlined />
                      </div>
                      <span className="text-xs font-bold uppercase tracking-wider text-gray-600">
                        Provider
                      </span>
                    </div>
                    <div
                      className="text-sm font-medium text-gray-900 truncate pl-1"
                      title={p.provider}
                    >
                      {p.provider || <MutedDefault />}
                    </div>
                  </div>
                  <div
                    className={`bg-white rounded-xl p-3.5 border shadow-sm flex flex-col transition-opacity duration-300 ${getOpacity("params", "model")}`}
                    style={{ borderColor: borderTint }}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div
                        className="w-7 h-7 rounded-full flex items-center justify-center text-sm"
                        style={{
                          backgroundColor: iconBgTint,
                          color: themeColor,
                        }}
                      >
                        <RobotOutlined />
                      </div>
                      <span className="text-xs font-bold uppercase tracking-wider text-gray-600">
                        Model
                      </span>
                    </div>
                    <div
                      className="text-sm font-medium text-gray-900 truncate pl-1"
                      title={p.model}
                    >
                      {p.model || <MutedDefault />}
                    </div>
                  </div>
                </div>

                {/* Row 2: Temperature & Tokens */}
                <div className="grid grid-cols-2 gap-3">
                  <div
                    className={`bg-white rounded-xl p-4 border shadow-sm flex flex-col relative transition-opacity duration-300 ${getOpacity("params", "temperature")}`}
                    style={{ borderColor: borderTint }}
                  >
                    <div className="flex items-center gap-2 text-gray-600 mb-4">
                      <DashboardOutlined style={{ color: themeColor }} />
                      <span className="text-xs font-bold tracking-wider uppercase">
                        Temperature
                      </span>
                    </div>
                    <div className="flex-1 flex flex-col items-center justify-center">
                      {p.temperature != null ? (
                        <Progress
                          type="circle"
                          percent={(p.temperature / 2.0) * 100}
                          size={72}
                          strokeColor={themeColor}
                          trailColor="#f1f5f9"
                          format={() => (
                            <span className="text-xl font-bold text-gray-800">
                              {p.temperature}
                            </span>
                          )}
                        />
                      ) : (
                        <MutedDefault />
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-3">
                    <div
                      className={`bg-white rounded-xl p-3 border shadow-sm flex flex-col flex-1 justify-center transition-opacity duration-300 ${getOpacity("params", "topP")}`}
                      style={{ borderColor: borderTint }}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center gap-1.5 text-gray-600">
                          <SlidersOutlined style={{ color: themeColor }} />
                          <span className="text-[11px] font-bold uppercase tracking-wider">
                            Top-P
                          </span>
                        </div>
                        <span className="text-sm font-bold text-gray-800">
                          {p.topP ?? <span className="text-gray-300">—</span>}
                        </span>
                      </div>
                      {p.topP != null && (
                        <Progress
                          percent={p.topP * 100}
                          showInfo={false}
                          size={["100%", 6]}
                          strokeColor={themeColor}
                          trailColor="#f1f5f9"
                          className="m-0"
                        />
                      )}
                    </div>
                    <div
                      className={`bg-white rounded-xl p-3 border shadow-sm flex flex-col flex-1 justify-center transition-opacity duration-300 ${getOpacity("params", "maxTokensPerSession")}`}
                      style={{ borderColor: borderTint }}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center gap-1.5 text-gray-600">
                          <AppstoreOutlined style={{ color: themeColor }} />
                          <span className="text-[11px] font-bold uppercase tracking-wider">
                            Max Tokens
                          </span>
                        </div>
                        <span className="text-sm font-bold text-gray-800">
                          {p.maxTokensPerSession ?? (
                            <span className="text-gray-300">—</span>
                          )}
                        </span>
                      </div>
                      {p.maxTokensPerSession != null && (
                        <Progress
                          percent={Math.min(
                            100,
                            (p.maxTokensPerSession / 128000) * 100,
                          )}
                          showInfo={false}
                          size={["100%", 6]}
                          strokeColor={themeColor}
                          trailColor="#f1f5f9"
                          className="m-0"
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* Row 4: Judge */}
                <div
                  className="rounded-xl p-4 border transition-opacity duration-300 mt-1"
                  style={{
                    backgroundColor: `${themeColor}05`,
                    borderColor: borderTint,
                  }}
                >
                  <div
                    className="text-[10px] font-bold uppercase tracking-widest mb-3 flex items-center gap-1.5"
                    style={{ color: themeColor }}
                  >
                    <SafetyCertificateOutlined />
                    Judge Settings
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div
                      className={`bg-white rounded-xl p-3 border border-white shadow-sm flex flex-col items-center justify-center transition-opacity duration-300 ${getOpacity("judge", "enabled")}`}
                    >
                      <span className="text-[10px] font-bold text-gray-500 uppercase mb-2">
                        Judge Enabled
                      </span>
                      {j.enabled ? (
                        <div className="flex items-center gap-1.5">
                          <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center shadow-[0_0_8px_rgba(34,197,94,0.4)]">
                            <div className="w-1.5 h-1.5 bg-white rounded-full" />
                          </div>
                          <span className="text-xs font-bold text-green-600">
                            Yes
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <div className="w-4 h-4 rounded-full bg-gray-300 flex items-center justify-center">
                            <div className="w-1.5 h-1.5 bg-white rounded-full" />
                          </div>
                          <span className="text-xs font-bold text-gray-500">
                            No
                          </span>
                        </div>
                      )}
                    </div>

                    <div
                      className={`bg-white rounded-xl p-3 border border-white shadow-sm flex flex-col items-center justify-center transition-opacity duration-300 ${getOpacity("judge", "mode")}`}
                    >
                      <span className="text-[10px] font-bold text-gray-500 uppercase mb-2">
                        Judge Mode
                      </span>
                      {j.mode ? (
                        <div className="flex items-center gap-1.5 bg-indigo-50 text-indigo-600 border border-indigo-100 rounded-md px-2 py-1 text-xs font-bold">
                          {j.mode === "image" ? (
                            <PictureOutlined />
                          ) : (
                            <FileTextOutlined />
                          )}
                          <span>{j.mode}</span>
                        </div>
                      ) : (
                        <MutedDefault />
                      )}
                    </div>

                    <div
                      className={`bg-white rounded-xl p-3 border border-white shadow-sm flex flex-col items-center justify-center transition-opacity duration-300 ${getOpacity("judge", "model")}`}
                    >
                      <span className="text-[10px] font-bold text-gray-500 uppercase mb-2">
                        Judge Model
                      </span>
                      {j.model ? (
                        <span
                          className="text-[11px] font-semibold text-center text-gray-700 break-all line-clamp-2 leading-tight"
                          title={j.model}
                        >
                          {j.model}
                        </span>
                      ) : (
                        <MutedDefault />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const StarRating = ({ value, color }: { value: number; color: string }) => {
  const stars = [];
  for (let i = 1; i <= 10; i++) {
    if (i <= Math.round(value)) {
      stars.push(<StarFilled key={i} style={{ color }} />);
    } else {
      stars.push(<StarOutlined key={i} className="text-gray-200" />);
    }
  }
  return <div className="flex gap-0.5 text-[10px]">{stars}</div>;
};

const MetricGauge = ({ title, r1, r2, v1, v2, def, tooltip }: any) => {
  return (
    <div className="flex flex-col gap-2 relative">
      <div className="flex items-center gap-1 mb-1">
        <Tooltip title={tooltip}>
          <span className="font-bold text-sm text-gray-800 cursor-help border-b border-dashed border-gray-300">
            {title}
          </span>
        </Tooltip>
      </div>
      <div className="flex justify-between items-center relative">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-full flex justify-center pointer-events-none">
          <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent w-full absolute top-1/2 -translate-y-1/2 -z-10" />
          <div className="bg-white/90 backdrop-blur px-2 py-0.5 rounded-full shadow-sm border border-gray-100/50">
            <DeltaBadge def={def} value={v2} baseline={v1} />
          </div>
        </div>
        <div className="flex flex-col items-center gap-1 z-0">
          <Progress
            type="dashboard"
            percent={v1 ? v1 * 100 : 0}
            gapDegree={140}
            strokeColor={r1.color}
            size={64}
            format={() => ""}
          />
          <div className="text-xs font-bold -mt-6" style={{ color: r1.color }}>
            {def.format(v1)}
          </div>
        </div>
        <div className="flex flex-col items-center gap-1 z-0">
          <Progress
            type="dashboard"
            percent={v2 ? v2 * 100 : 0}
            gapDegree={140}
            strokeColor={r2.color}
            size={64}
            format={() => ""}
          />
          <div className="text-xs font-bold -mt-6" style={{ color: r2.color }}>
            {def.format(v2)}
          </div>
        </div>
      </div>
    </div>
  );
};

const MetricStars = ({ title, r1, r2, v1, v2, def, tooltip }: any) => {
  return (
    <div className="flex flex-col gap-2 relative mt-4">
      <div className="flex items-center gap-1 mb-1">
        <Tooltip title={tooltip}>
          <span className="font-bold text-sm text-gray-800 cursor-help border-b border-dashed border-gray-300">
            {title}
          </span>
        </Tooltip>
      </div>
      <div className="flex justify-between items-center relative">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-full flex justify-center pointer-events-none">
          <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent w-full absolute top-1/2 -translate-y-1/2 -z-10" />
          <div className="bg-white/90 backdrop-blur px-2 py-0.5 rounded-full shadow-sm border border-gray-100/50">
            <DeltaBadge def={def} value={v2} baseline={v1} />
          </div>
        </div>
        <div className="flex flex-col items-center gap-1 z-0">
          <StarRating value={v1 || 0} color={r1.color} />
          <div className="text-xs font-bold" style={{ color: r1.color }}>
            {def.format(v1)}
          </div>
        </div>
        <div className="flex flex-col items-center gap-1 z-0">
          <StarRating value={v2 || 0} color={r2.color} />
          <div className="text-xs font-bold" style={{ color: r2.color }}>
            {def.format(v2)}
          </div>
        </div>
      </div>
    </div>
  );
};

const MetricCircle = ({ title, r1, r2, v1, v2, def, tooltip }: any) => {
  return (
    <div className="flex flex-col gap-2 relative mt-6">
      <div className="flex items-center gap-1 mb-1">
        <Tooltip title={tooltip}>
          <span className="font-bold text-sm text-gray-800 cursor-help border-b border-dashed border-gray-300">
            {title}
          </span>
        </Tooltip>
      </div>
      <div className="flex justify-between items-center relative px-2">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-full flex justify-center pointer-events-none">
          <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent w-full absolute top-1/2 -translate-y-1/2 -z-10" />
          <div className="bg-white/90 backdrop-blur px-2 py-0.5 rounded-full shadow-sm border border-gray-100/50">
            <DeltaBadge def={def} value={v2} baseline={v1} />
          </div>
        </div>
        <div className="flex flex-col items-center z-0">
          <Progress
            type="circle"
            percent={v1 ? v1 * 100 : 0}
            strokeColor={r1.color}
            size={48}
            format={() => ""}
          />
          <div
            className="text-xs font-bold absolute mt-4"
            style={{ color: r1.color }}
          >
            {def.format(v1)}
          </div>
        </div>
        <div className="flex flex-col items-center z-0">
          <Progress
            type="circle"
            percent={v2 ? v2 * 100 : 0}
            strokeColor={r2.color}
            size={48}
            format={() => ""}
          />
          <div
            className="text-xs font-bold absolute mt-4"
            style={{ color: r2.color }}
          >
            {def.format(v2)}
          </div>
        </div>
      </div>
    </div>
  );
};

const MetricBarChart = ({ title, r1, r2, v1, v2, def, tooltip }: any) => {
  const max = Math.max(v1 || 0, v2 || 0) * 1.2 || 1;
  const h1 = ((v1 || 0) / max) * 100;
  const h2 = ((v2 || 0) / max) * 100;

  return (
    <div className="flex flex-col gap-2 relative flex-1">
      <div className="flex items-center justify-center mb-1">
        <Tooltip title={tooltip}>
          <span className="font-bold text-sm text-gray-800 cursor-help border-b border-dashed border-gray-300">
            {title}
          </span>
        </Tooltip>
      </div>
      <div className="flex justify-center h-32 relative">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 z-10 flex justify-center pointer-events-none w-full">
          <div className="bg-white/90 backdrop-blur px-2 py-0.5 rounded-full shadow-sm border border-gray-100/50">
            <DeltaBadge def={def} value={v2} baseline={v1} />
          </div>
        </div>
        <div className="flex items-end justify-center gap-6 h-full w-full border-b-2 border-gray-100 pb-1 px-4">
          <div className="flex flex-col items-center gap-1 z-0 group">
            <span className="text-[10px] font-semibold text-gray-600 group-hover:text-gray-900 transition-colors">
              {def.format(v1)}
            </span>
            <div
              className="w-10 rounded-t-md transition-all shadow-sm relative overflow-hidden"
              style={{
                height: `${Math.max(5, h1)}%`,
                backgroundColor: r1.color,
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent" />
            </div>
            <span
              className="w-5 h-5 rounded text-[10px] font-bold text-white flex items-center justify-center shadow-sm"
              style={{ backgroundColor: r1.color }}
            >
              A
            </span>
          </div>
          <div className="flex flex-col items-center gap-1 z-0 group">
            <span className="text-[10px] font-semibold text-gray-600 group-hover:text-gray-900 transition-colors">
              {def.format(v2)}
            </span>
            <div
              className="w-10 rounded-t-md transition-all shadow-sm relative overflow-hidden"
              style={{
                height: `${Math.max(5, h2)}%`,
                backgroundColor: r2.color,
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent" />
            </div>
            <span
              className="w-5 h-5 rounded text-[10px] font-bold text-white flex items-center justify-center shadow-sm"
              style={{ backgroundColor: r2.color }}
            >
              B
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const CoinStack = ({
  count,
  color,
  label,
}: {
  count: number;
  color: string;
  label: string;
}) => {
  const capped = Math.min(15, count);
  return (
    <div className="flex flex-col items-center gap-1">
      <div
        className="relative w-12 flex justify-center"
        style={{ height: capped * 5 + 16 }}
      >
        {Array.from({ length: capped }).map((_, i) => (
          <div
            key={i}
            className="w-10 h-4 rounded-full border border-black/10 absolute shadow-sm"
            style={{ backgroundColor: color, bottom: i * 5, zIndex: i }}
          />
        ))}
      </div>
      <div className="text-xs font-bold text-gray-700">{label}</div>
    </div>
  );
};

const _SmartMetricsCompareView = ({
  runs,
  overallWinnerId,
}: {
  runs: CompareRun[];
  overallWinnerId: string | null;
}) => {
  const [r1, r2] = runs;

  const getV = (r: CompareRun, key: string) => runMetric(r.run, key as any);
  const getDef = (key: string) => METRICS.find((m) => m.key === key);

  return (
    <div className="space-y-4">
      {/* Run headers */}
      <div className="grid grid-cols-2 gap-4">
        {runs.map((r, idx) => (
          <div
            key={r.id}
            className="rounded-xl px-4 py-3 flex items-center gap-3 shadow-sm border"
            style={{
              backgroundColor: `${r.color}05`,
              borderColor: `${r.color}30`,
            }}
          >
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center text-white shadow-sm"
              style={{ backgroundColor: r.color }}
            >
              <ApiOutlined />
            </div>
            <span
              className="font-bold text-sm tracking-wider uppercase truncate"
              style={{ color: r.color }}
            >
              MODEL {idx === 0 ? "A" : "B"}: {r.label}
            </span>
            {overallWinnerId === r.id && (
              <TrophyFilled className="text-amber-400 text-lg ml-auto drop-shadow-sm" />
            )}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* QUALITY & ACCURACY */}
        <div className="rounded-2xl bg-white border border-indigo-100/50 shadow-[0_4px_20px_rgb(0,0,0,0.03)] p-5 flex flex-col relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4 flex gap-3 text-3xl text-indigo-100 opacity-60">
            <SearchOutlined />
            <SafetyCertificateOutlined />
          </div>
          <h3 className="font-bold text-sm uppercase tracking-widest text-slate-800 mb-6">
            Quality & Accuracy
          </h3>

          <MetricGauge
            title="Accuracy"
            tooltip="The percentage of correct responses, where higher indicates outputs closely matched expected truth."
            r1={r1}
            r2={r2}
            v1={getV(r1, "accuracyRate")}
            v2={getV(r2, "accuracyRate")}
            def={getDef("accuracyRate")}
          />
          <MetricStars
            title="Avg Judge"
            tooltip="The average score (out of 10) awarded by the LLM-as-a-judge for subjective response quality."
            r1={r1}
            r2={r2}
            v1={getV(r1, "averageQualityScore")}
            v2={getV(r2, "averageQualityScore")}
            def={getDef("averageQualityScore")}
          />
          <MetricCircle
            title="Success Rate"
            tooltip="The percentage of requests that completed successfully without errors or timeouts."
            r1={r1}
            r2={r2}
            v1={getV(r1, "successRate")}
            v2={getV(r2, "successRate")}
            def={getDef("successRate")}
          />
        </div>

        {/* PERFORMANCE */}
        <div className="rounded-2xl bg-white border border-sky-100/50 shadow-[0_4px_20px_rgb(0,0,0,0.03)] p-5 flex flex-col relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4 flex gap-3 text-3xl text-sky-100 opacity-60">
            <FieldTimeOutlined />
            <DashboardOutlined />
          </div>
          <h3 className="font-bold text-sm uppercase tracking-widest text-slate-800 mb-6">
            Performance
          </h3>

          <div className="flex flex-col gap-6 flex-1">
            <MetricBarChart
              title="Avg Latency"
              tooltip="The average time taken for the model to generate the full response."
              r1={r1}
              r2={r2}
              v1={getV(r1, "averageLatencyMs")}
              v2={getV(r2, "averageLatencyMs")}
              def={getDef("averageLatencyMs")}
            />
            <MetricBarChart
              title="P95 Latency"
              tooltip="The 95th percentile latency; 95% of requests completed faster than this time."
              r1={r1}
              r2={r2}
              v1={getV(r1, "p95LatencyMs")}
              v2={getV(r2, "p95LatencyMs")}
              def={getDef("p95LatencyMs")}
            />
          </div>
        </div>

        {/* COST & EFFICIENCY */}
        <div className="rounded-2xl bg-white border border-emerald-100/50 shadow-[0_4px_20px_rgb(0,0,0,0.03)] p-5 flex flex-col relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4 flex gap-3 text-3xl text-emerald-100 opacity-60">
            <DollarCircleOutlined />
            <AccountBookOutlined />
          </div>
          <h3 className="font-bold text-sm uppercase tracking-widest text-slate-800 mb-6">
            Cost & Efficiency
          </h3>

          <div className="flex justify-between items-end mt-4 px-2 relative">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-full flex justify-center pointer-events-none">
              <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent w-full absolute top-1/2 -translate-y-1/2 -z-10" />
              <div className="bg-white/90 backdrop-blur px-2 py-0.5 rounded-full shadow-sm border border-gray-100/50">
                <DeltaBadge
                  def={getDef("totalTokens")!}
                  value={getV(r2, "totalTokens")}
                  baseline={getV(r1, "totalTokens")}
                />
              </div>
            </div>
            <CoinStack
              count={5}
              color={r1.color || "#ccc"}
              label={
                getDef("totalTokens")?.format(getV(r1, "totalTokens")!) || "0"
              }
            />
            <CoinStack
              count={3}
              color={r2.color || "#ccc"}
              label={
                getDef("totalTokens")?.format(getV(r2, "totalTokens")!) || "0"
              }
            />
          </div>
          <div className="flex justify-center mt-2 mb-6">
            <Tooltip title="The total number of prompt and completion tokens processed.">
              <span className="text-xs font-bold text-gray-500 uppercase tracking-widest cursor-help border-b border-dashed border-gray-300">
                Total Tokens
              </span>
            </Tooltip>
          </div>

          <div className="flex justify-between items-end mt-4 px-2 relative">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-full flex justify-center pointer-events-none">
              <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent w-full absolute top-1/2 -translate-y-1/2 -z-10" />
              <div className="bg-white/90 backdrop-blur px-2 py-0.5 rounded-full shadow-sm border border-gray-100/50">
                <DeltaBadge
                  def={getDef("totalCost")!}
                  value={getV(r2, "totalCost")}
                  baseline={getV(r1, "totalCost")}
                />
              </div>
            </div>
            <CoinStack
              count={7}
              color={r1.color || "#ccc"}
              label={
                getDef("totalCost")?.format(getV(r1, "totalCost")!) || "$0"
              }
            />
            <CoinStack
              count={2}
              color={r2.color || "#ccc"}
              label={
                getDef("totalCost")?.format(getV(r2, "totalCost")!) || "$0"
              }
            />
          </div>
          <div className="flex justify-center mt-2">
            <Tooltip title="The estimated total API cost for running these cases.">
              <span className="text-xs font-bold text-gray-500 uppercase tracking-widest cursor-help border-b border-dashed border-gray-300">
                Total Cost
              </span>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  );
};

export const CompareOverview = ({
  runs,
  matrix,
  baselineId,
  agents = [],
}: {
  runs: CompareRun[];
  matrix: CompareMatrix;
  baselineId: string;
  agents?: any[];
}) => {
  const baseline = runs.find((r) => r.id === baselineId)?.run ?? null;
  const [diffOnly, setDiffOnly] = useState(false);

  // Derive absolute configuration for each run
  const absoluteConfigs = useMemo(() => {
    return runs.map((r) => {
      const agentId =
        typeof r.run.agent === "string"
          ? r.run.agent
          : r.run.agent?.id || (r.run.agent as any)?.["@id"];
      const agent =
        agents?.find((a: any) => (a.id || a["@id"])?.includes(agentId)) ||
        (typeof r.run.agent !== "string" ? r.run.agent : undefined);

      const overrides = (r.run.config as any)?.parameters || {};
      const gen = effectiveInferenceParams(overrides);
      const agentOpts: Record<string, any> = agent?.aiConnection?.options ?? {};
      const agentConnection =
        (r.run.config as any)?.agentAiConnection || agent?.aiConnection;

      const overrideProvider = agentConnection?.provider;
      const overrideModel = agentConnection?.model;

      const absParams = {
        provider: overrideProvider || agent?.aiConnection?.provider,
        model: gen.model || overrideModel || agent?.aiConnection?.model,
        // Generation params live in the run's inference-options override (snake_case), already resolved
        // against the agent's base connection by the backend. Fall back to the agent's own options.
        temperature: gen.temperature ?? agentOpts.temperature,
        topP: gen.top_p ?? agentOpts.topP,
        maxTokensPerSession:
          overrides.maxTokensPerSession ?? agent?.maxTokensPerSession,
      };
      const rawJudge = (r.run.config as any)?.judge || {};
      // The judge's model comes from its selected AI profile and is not snapshotted into the config,
      // so it can no longer be derived from the config alone.
      const absJudge = { ...rawJudge };
      return { id: r.id, params: absParams, judge: absJudge };
    });
  }, [runs, agents]);

  const overallWinnerId = useMemo(() => {
    const wins: Record<string, number> = {};
    for (const def of METRICS) {
      const winner = bestRunId(def, runs);
      if (winner) wins[winner] = (wins[winner] || 0) + 1;
    }
    const entries = Object.entries(wins);
    if (entries.length === 0) return null;
    entries.sort((a, b) => b[1] - a[1]);
    const bestCount = entries[0][1];
    const topRuns = entries.filter((e) => e[1] === bestCount);
    // Only crown a single overall winner if they strictly have the most wins
    return topRuns.length === 1 ? topRuns[0][0] : null;
  }, [runs]);

  // "Who leads where" — group headline metrics by winning run.
  const insight = useMemo(() => {
    const headline = METRICS.filter((m) =>
      [
        "accuracyRate",
        "averageQualityScore",
        "averageLatencyMs",
        "totalCost",
      ].includes(m.key),
    );
    const byRun = new Map<string, string[]>();
    for (const def of headline) {
      const winner = bestRunId(def, runs);
      if (!winner) continue;
      // Only credit a clear win (someone else has a value too).
      const valued = runs.filter((r) => runMetric(r.run, def.key) != null);
      if (valued.length < 2) continue;
      (byRun.get(winner) ?? byRun.set(winner, []).get(winner)!).push(
        def.label.toLowerCase(),
      );
    }
    return Array.from(byRun.entries())
      .map(([id, metrics]) => ({ run: runs.find((r) => r.id === id), metrics }))
      .filter((x) => x.run);
  }, [runs]);

  if (runs.length < 2) return <Empty description="Select at least two runs." />;

  return (
    <div className="space-y-5">
      {/* Run headers */}
      <div
        className="grid gap-3"
        style={{
          gridTemplateColumns: `repeat(${runs.length}, minmax(0, 1fr))`,
        }}
      >
        {runs.map((r) => (
          <Card
            key={r.id}
            className="shadow-sm border-t-4"
            style={{ borderTopColor: r.color }}
            styles={{ body: { padding: 14 } }}
          >
            <div className="flex items-center justify-between mb-2">
              <span
                className="font-semibold text-gray-800 truncate"
                title={r.label}
              >
                {r.label}
              </span>
              {r.id === baselineId ? (
                <Tag>baseline</Tag>
              ) : (
                statusTag(r.run.status)
              )}
            </div>
            <div className="text-xs text-gray-500 mb-2">
              {r.run.completedCases}/{r.run.totalCases} cases
              {r.run.failedCases > 0 && (
                <span className="text-rose-500">
                  {" "}
                  · {r.run.failedCases} errored
                </span>
              )}
            </div>
            <BenchmarkConfigDetails config={r.run.config} />
          </Card>
        ))}
      </div>

      <div className="text-xs text-gray-500">
        {matrix.rows.length} case{matrix.rows.length === 1 ? "" : "s"} compared
        · {matrix.sharedCount} shared by all runs
      </div>

      {/* Auto insight */}
      {insight.length > 0 && (
        <Card
          className="shadow-[0_4px_20px_rgb(0,0,0,0.03)] border border-gray-200/60 !rounded-2xl overflow-hidden transition-all hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] mb-8"
          styles={{ body: { padding: 16 } }}
        >
          <div className="flex items-start gap-2 text-sm">
            <BulbOutlined className="text-amber-500 mt-0.5" />
            <div className="flex flex-col gap-y-1">
              {insight.map(({ run, metrics }) => (
                <span key={run!.id}>
                  <span className="font-semibold" style={{ color: run!.color }}>
                    {run!.label}
                  </span>{" "}
                  leads on {metrics.join(", ")}.
                </span>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Configuration Comparison Grid */}
      <Card
        className="shadow-[0_4px_20px_rgb(0,0,0,0.03)] border border-gray-200/60 !rounded-2xl overflow-hidden transition-all hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)]"
        styles={{ body: { padding: 0 } }}
        title={
          <div className="flex items-center justify-between text-sm py-1">
            <span className="font-semibold text-gray-800">
              Absolute Configuration
            </span>
            <div className="flex items-center gap-2">
              <span className="text-gray-500 font-normal text-xs">
                Highlight only the differences
              </span>
              <Switch size="small" checked={diffOnly} onChange={setDiffOnly} />
            </div>
          </div>
        }
      >
        <SmartCardCompareView
          runs={runs}
          absoluteConfigs={absoluteConfigs}
          diffOnly={diffOnly}
        />
      </Card>

      {runs.length === 2 ? (
        <SmartMetricsCompareView
          runs={runs}
          overallWinnerId={overallWinnerId}
        />
      ) : (
        <>
          {/* Metric comparison grid */}
          <Card
            className="shadow-[0_4px_20px_rgb(0,0,0,0.03)] border border-gray-200/60 !rounded-2xl overflow-hidden transition-all hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)]"
            title={
              <span className="text-[13px] font-bold uppercase tracking-wider text-gray-800">
                Metrics
              </span>
            }
            styles={{ body: { padding: 0 } }}
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/60">
                    <th className="text-left px-4 py-2 font-semibold text-gray-500">
                      Metric
                    </th>
                    {runs.map((r) => (
                      <th key={r.id} className="text-left px-4 py-2">
                        <span className="flex items-center gap-1.5">
                          <span
                            className="w-2.5 h-2.5 rounded-full inline-block"
                            style={{ backgroundColor: r.color }}
                          />
                          <span
                            className="truncate max-w-[10rem]"
                            title={r.label}
                          >
                            {r.label}
                          </span>
                          {overallWinnerId === r.id && runs.length > 1 && (
                            <Tooltip title="Overall Winner">
                              <TrophyFilled className="text-amber-400 text-sm ml-1" />
                            </Tooltip>
                          )}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Array.from(new Set(METRICS.map((m) => m.category))).map(
                    (cat) => (
                      <Fragment key={cat}>
                        <tr className="bg-gray-50/40">
                          <td
                            colSpan={runs.length + 1}
                            className="px-4 py-2 text-xs font-bold text-gray-600 uppercase tracking-wider border-b border-gray-100"
                          >
                            {cat}
                          </td>
                        </tr>
                        {METRICS.filter((m) => m.category === cat).map(
                          (def) => {
                            const baseVal = baseline
                              ? runMetric(baseline, def.key)
                              : null;
                            return (
                              <tr
                                key={def.key}
                                className="border-b border-gray-50 last:border-0 hover:bg-gray-50/30 transition-colors"
                              >
                                <td className="px-4 py-2 text-gray-500 pl-6">
                                  {def.label}
                                </td>
                                {runs.map((r) => {
                                  const v = runMetric(r.run, def.key);
                                  return (
                                    <td key={r.id} className="px-4 py-2">
                                      <span className="font-semibold text-gray-800">
                                        {def.format(v)}
                                      </span>
                                      {r.id !== baselineId && (
                                        <DeltaBadge
                                          def={def}
                                          value={v}
                                          baseline={baseVal}
                                        />
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          },
                        )}
                      </Fragment>
                    ),
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Charts */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            {METRICS.filter((m) =>
              [
                "accuracyRate",
                "averageQualityScore",
                "averageLatencyMs",
                "totalCost",
              ].includes(m.key),
            ).map((def) => (
              <MiniBarChart key={def.key} def={def} runs={runs} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default CompareOverview;
